import type React from 'react';
import {
  type FC,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { ScrollBlock } from '../ScrollBlock';

import { ScTable } from './styled';

const translationPath = (path: string, key: string) =>
  path.indexOf(':') === -1 ? `${path}:${key}` : `${path}.${key}`;

export type Row = { [key: string]: React.ReactNode };

export interface TableProps extends Omit<TableBodyProps, 'rows'> {
  data: Row[];
  increment?: boolean;
  mobileSupport?: boolean;
  mode?: ThemeMode;
}

export function Table({
  data,
  increment,
  maxHeight,
  columnWidth,
  headersTransPath,
  mobileSupport,
  mode = ThemeMode.Dark,
  ...rest
}: TableProps) {
  const { t, i18n } = useTranslation();

  const [widthColumns, setWidthColumns] =
    useState<TableHeadProps['widthColumns']>(undefined);

  const { headers, rows } = useMemo(() => {
    if (data.length === 0) return { headers: [], rows: [] };

    const headersTransl =
      headersTransPath == null
        ? Object.keys(data[0])
        : Object.keys(data[0]).map((header) => {
            const translPath = translationPath(headersTransPath, header);

            return i18n.exists(translPath) ? t(translPath) : header;
          });

    if (increment == null) {
      return {
        headers: headersTransl,
        rows: data,
      };
    }

    return {
      headers: ['#', ...headersTransl],
      rows: data.map((row, index) => ({ index: index + 1, ...row })),
    };
  }, [data, increment, headersTransPath, t, i18n]);

  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const setColumns = useCallback(() => {
    if (maxHeight == null || tbodyRef.current?.firstChild == null) return;

    const wc: number[] = Array.from(tbodyRef.current.firstChild.childNodes).map(
      (e) => (e as HTMLTableSectionElement).clientWidth
    );

    setWidthColumns(wc);
  }, [tbodyRef, maxHeight, setWidthColumns]);

  useEffect(() => {
    setColumns();
    window.addEventListener('resize', setColumns);
    return () => {
      window.removeEventListener('resize', setColumns);
    };
  }, [setColumns]);

  return (
    <ScTable mobile={mobileSupport} mode={mode} withScroll={maxHeight != null}>
      <TableHead
        headers={headers}
        widthColumns={
          columnWidth ? columnWidth.map((e) => `${e}%`) : widthColumns
        }
      />
      <TableBody
        columnWidth={columnWidth}
        headersTransPath={headersTransPath}
        maxHeight={maxHeight}
        ref={tbodyRef}
        rows={rows}
        {...rest}
      />
    </ScTable>
  );
}

interface TableHeadProps {
  headers: string[];
  widthColumns: number[] | string[] | undefined;
}

const TableHead: FC<TableHeadProps> = ({ headers, widthColumns }) => (
  <thead>
    <tr>
      {headers.map((header, index) => (
        <th
          key={index}
          style={{
            width: widthColumns == null ? 'auto' : widthColumns[index],
          }}
        >
          {capitalize(header)}
        </th>
      ))}
    </tr>
  </thead>
);

interface TableBodyProps {
  rows: Row[];
  maxHeight?: number;
  columnWidth?: number[]; // measured as a percentage
  perChunk?: number;
  rowsTransPath?: string;
  headersTransPath?: string;
  activeRowIndex?: number;
}

const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  (
    {
      rows,
      maxHeight,
      columnWidth,
      perChunk,
      rowsTransPath,
      headersTransPath,
      activeRowIndex,
    },
    ref
  ) => {
    const { t, i18n } = useTranslation();

    const [currentChunk, setCurrentChunk] = useState(1);

    const rowsChunk = useMemo(() => {
      if (perChunk == null) return rows;
      return rows.slice(0, currentChunk * perChunk);
    }, [rows, currentChunk, perChunk]);

    const handleNextChunk = () => {
      setCurrentChunk((s) => s + 1);
    };

    const visibleButtonShowMore = useMemo(() => {
      const rowsChunkCount = rowsChunk.length;
      const rowsCount = rows.length;

      return rowsCount - rowsChunkCount !== 0;
    }, [rows, rowsChunk]);

    const countColumns = useMemo(() => {
      if (rowsChunk.length === 0) return 1;

      return Object.values(rowsChunk[0]).length || 1;
    }, [rowsChunk]);

    const rowsComp = (
      <tbody ref={ref}>
        {rowsChunk.map((row, index) => (
          <tr
            className={activeRowIndex === index ? 'active-row' : ''}
            key={index}
          >
            {Object.entries(row).map(([key, value], i) => (
              <td
                data-head={
                  headersTransPath == null ||
                  !i18n.exists(`${headersTransPath}.${key}`)
                    ? key
                    : t(`${headersTransPath}.${key}`)
                }
                key={i}
                width={columnWidth == null ? 'auto' : `${columnWidth[index]}%`}
              >
                {rowsTransPath == null
                  ? value
                  : t(
                      `${rowsTransPath.replace('{}', index.toString())}.${key}`
                    )}
              </td>
            ))}
          </tr>
        ))}
        {visibleButtonShowMore && (
          <tr>
            <td colSpan={countColumns}>
              <button
                className="button-show-more"
                onClick={handleNextChunk}
                type="button"
              >
                {t('showMore')}
              </button>
            </td>
          </tr>
        )}
      </tbody>
    );

    return maxHeight == null ? (
      rowsComp
    ) : (
      <ScrollBlock height={`${maxHeight}px`} scrollTopMargin={4} width="100%">
        {rowsComp}
      </ScrollBlock>
    );
  }
);
